/** Align rally vs march countdown with server `finalizeRallyPhaseByAttackId` / `getRallyPhaseEndMs`. */

export const MVM_RALLY_JOIN_WINDOW_MS = 3 * 60 * 1000

/** Same as backend `MVM_TRAVEL_ARRIVAL_TOLERANCE_MS` — arrival is server-authoritative. */
export const MVM_TRAVEL_ARRIVAL_TOLERANCE_MS = 0

export type MvMPendingLike = {
  status?: string
  arrivesAt?: string
  /** After castle battle: attackers march home; same duration as outbound march. */
  returnArrivesAt?: string
  startedAt?: string
  rallyPhaseEndAt?: string
  marchTravelTimeMs?: number
  travelTimeMs?: number
  rallyJoiners?: Array<{ walletAddress?: string; selectedCharacterIds?: unknown[] }>
}

/** Per-wallet march leg from `GET /mvm/pending-attacks` (`pendingAttackModel.marchLegs`). */
export type MarchLegApi = {
  legId?: string
  walletAddress?: string
  side?: 'attacker' | 'defender'
  role?: string
  fromQ?: number
  fromR?: number
  toQ?: number
  toR?: number
  startedAt?: string
  arrivesAt?: string
  marchTravelTimeMs?: number
  status?: string
  selectedCharacterIds?: string[]
  characterTemplateIds?: string[]
  abilityPoints?: { totalBudget?: number }
}

/**
 * Per-ally home march after a siege ends (`pendingAttackModel.allyReturnMarches`). Coords are already
 * in return direction (from = besieged castle, to = the ally's own home). Dedicated field — NOT a leg.
 */
export type AllyReturnMarchApi = {
  walletAddress?: string
  side?: 'attacker' | 'defender'
  role?: 'rally_joiner' | 'reinforcement'
  fromQ?: number
  fromR?: number
  toQ?: number
  toR?: number
  returnArrivesAt?: string
  marchMs?: number
  characterTemplateIds?: string[]
}

export type MarchLegPendingApi = MvMPendingLike & {
  attackId: string
  attackerWallet?: string
  defenderWallet?: string
  defenderNickName?: string
  rallyMode?: boolean
  marchLegs?: MarchLegApi[]
  allyReturnMarches?: AllyReturnMarchApi[]
  attackType?: string
}

function normWalletMvM(w: string | undefined): string {
  return (w || '').toLowerCase().trim()
}

export function pendingUsesMarchLegsApi(atk: { rallyMode?: boolean; marchLegs?: MarchLegApi[] }): boolean {
  // Leg presence, not rallyMode: a direct attack gains legs once a defender ally reinforces.
  return !!(Array.isArray(atk.marchLegs) && atk.marchLegs.length > 0)
}

/**
 * True only when the ATTACKER's own outbound march is a leg (rally leader / rally joiner). A DIRECT
 * attack whose only legs are defender reinforcements returns FALSE — its attacker still uses the legacy
 * single march arrow, so that arrow must NOT be removed or skipped when the reinforcement legs are drawn.
 */
export function attackerUsesMarchLegsApi(atk: { marchLegs?: MarchLegApi[] }): boolean {
  return !!(Array.isArray(atk.marchLegs) && atk.marchLegs.some((l) => l?.side === 'attacker'))
}

/**
 * Who should see a traveling leg line on the world map.
 *
 * Siege march lines are PUBLIC: every world-map viewer sees all converging armies — the rally leader,
 * every rally joiner, late allies, and the defender's reinforcements — not just the main attacker /
 * main defender. This keeps the live socket path in step with the reconnect path
 * ({@link collectPublicMarchLegArrowDraws}), which already drew every leg for everyone. The only
 * filtering left is basic validity (a real viewer + a leg that has an owner wallet).
 */
export function viewerCanSeeMarchLeg(
  leg: MarchLegApi,
  _atk: Pick<MarchLegPendingApi, 'attackerWallet' | 'defenderWallet'>,
  viewerWallet: string,
): boolean {
  return !!normWalletMvM(viewerWallet) && !!normWalletMvM(leg.walletAddress)
}

export type MarchLegArrowDraw = {
  attackId: string
  legId: string
  fromQ: number
  fromR: number
  toQ: number
  toR: number
  travelTimeMinutes: number
  targetOwner?: string
  targetName?: string
  charIds?: string[]
  abilityBudget?: number
  templateIds?: string[]
  mvmAttackType?: 'castle' | 'tower_pvp'
  arrowTime: MvmArrowRestoreTime
}

/** Build draw params for each traveling leg the viewer should see (leg-based castle rallies). */
export function collectMarchLegArrowDraws(
  attacks: MarchLegPendingApi[],
  viewerWallet: string,
  now: number,
  arrivalBufferMs: number,
  defenderLabel: (atk: MarchLegPendingApi) => string,
): MarchLegArrowDraw[] {
  const out: MarchLegArrowDraw[] = []
  const viewer = normWalletMvM(viewerWallet)
  if (!viewer) return out

  for (const atk of attacks) {
    if (!pendingUsesMarchLegsApi(atk)) continue
    for (const leg of atk.marchLegs || []) {
      if (String(leg.status || '').toLowerCase() !== 'traveling') continue
      if (!viewerCanSeeMarchLeg(leg, atk, viewer)) continue
      const legId = String(leg.legId || '').trim()
      if (!legId) continue
      const fromQ = Number(leg.fromQ)
      const fromR = Number(leg.fromR)
      const toQ = Number(leg.toQ)
      const toR = Number(leg.toR)
      if (![fromQ, fromR, toQ, toR].every(Number.isFinite)) continue
      const arrMs = parseIsoMs(leg.arrivesAt)
      if (!Number.isFinite(arrMs) || arrMs <= now) continue
      const marchMs =
        Number(leg.marchTravelTimeMs) > 0
          ? Number(leg.marchTravelTimeMs)
          : Number(atk.marchTravelTimeMs) > 0
            ? Number(atk.marchTravelTimeMs)
            : Number(atk.travelTimeMs) > 0
              ? Number(atk.travelTimeMs)
              : 0
      const arrowTime = mvmRestoreSimpleMarchWindow({
        arrivalEndMs: arrMs,
        marchDurationMs: marchMs,
        now,
        arrivalBufferMs,
      })
      if (!arrowTime) continue
      const remMin = Math.max((arrMs + arrivalBufferMs - now) / 60000, 0.01)
      const charIds = (leg.selectedCharacterIds || []).map((id) => String(id))
      const isDefLeg = leg.side === 'defender'
      out.push({
        attackId: atk.attackId,
        legId,
        fromQ,
        fromR,
        toQ,
        toR,
        travelTimeMinutes: remMin,
        targetOwner: isDefLeg ? atk.attackerWallet : atk.defenderWallet,
        targetName: isDefLeg ? '' : defenderLabel(atk),
        charIds: charIds.length ? charIds : undefined,
        abilityBudget: leg.abilityPoints?.totalBudget,
        templateIds: leg.characterTemplateIds,
        mvmAttackType: atk.attackType === 'tower_pvp' ? 'tower_pvp' : 'castle',
        arrowTime,
      })
    }
  }
  return out
}

/** All traveling legs — passive world-map viewers (`getActiveAttacks` reconnect). */
export function collectPublicMarchLegArrowDraws(
  attacks: MarchLegPendingApi[],
  now: number,
  arrivalBufferMs: number,
  defenderLabel: (atk: MarchLegPendingApi) => string,
): MarchLegArrowDraw[] {
  const out: MarchLegArrowDraw[] = []
  for (const atk of attacks) {
    if (!pendingUsesMarchLegsApi(atk)) continue
    for (const leg of atk.marchLegs || []) {
      if (String(leg.status || '').toLowerCase() !== 'traveling') continue
      const legId = String(leg.legId || '').trim()
      if (!legId) continue
      const fromQ = Number(leg.fromQ)
      const fromR = Number(leg.fromR)
      const toQ = Number(leg.toQ)
      const toR = Number(leg.toR)
      if (![fromQ, fromR, toQ, toR].every(Number.isFinite)) continue
      const arrMs = parseIsoMs(leg.arrivesAt)
      if (!Number.isFinite(arrMs) || arrMs <= now) continue
      const marchMs =
        Number(leg.marchTravelTimeMs) > 0
          ? Number(leg.marchTravelTimeMs)
          : Number(atk.marchTravelTimeMs) > 0
            ? Number(atk.marchTravelTimeMs)
            : Number(atk.travelTimeMs) > 0
              ? Number(atk.travelTimeMs)
              : 0
      const arrowTime = mvmRestoreSimpleMarchWindow({
        arrivalEndMs: arrMs,
        marchDurationMs: marchMs,
        now,
        arrivalBufferMs,
      })
      if (!arrowTime) continue
      const remMin = Math.max((arrMs + arrivalBufferMs - now) / 60000, 0.01)
      const isDefLeg = leg.side === 'defender'
      const charIds = (leg.selectedCharacterIds || []).map((id) => String(id))
      out.push({
        attackId: atk.attackId,
        legId,
        fromQ,
        fromR,
        toQ,
        toR,
        travelTimeMinutes: remMin,
        targetOwner: isDefLeg ? atk.attackerWallet : atk.defenderWallet,
        targetName: isDefLeg ? '' : defenderLabel(atk),
        charIds: charIds.length ? charIds : undefined,
        abilityBudget: leg.abilityPoints?.totalBudget,
        templateIds: leg.characterTemplateIds,
        mvmAttackType: atk.attackType === 'tower_pvp' ? 'tower_pvp' : 'castle',
        arrowTime,
      })
    }
  }
  return out
}

/** A static combat line to restore for a siege whose army has already landed. */
export type EngagedCombatLineDraw = {
  attackId: string
  /** Per-leg key (`leg_*`) for ally lines; omitted for the main attacker's single line. */
  arrowKeySuffix?: string
  fromQ: number
  fromR: number
  toQ: number
  toR: number
  targetOwner?: string
  targetName?: string
  templateIds?: string[]
  mvmAttackType?: 'castle' | 'tower_pvp'
  /** Server-epoch ms the march landed, so the line is not treated as freshly engaged on every hydrate. */
  engagedAtMs?: number
}

/** Pending statuses in which the armies are at the castle and the fight is live — the combat line shows. */
const ENGAGED_COMBAT_LINE_STATUSES = new Set([
  'arrived',
  'character_selection',
  'battle_creating',
  'battle_started',
])

export function isEngagedCombatLineStatus(status: string | undefined): boolean {
  return ENGAGED_COMBAT_LINE_STATUSES.has(String(status || '').toLowerCase())
}

/**
 * Rebuild the combat lines of sieges that are already in battle (world-map mount / reconnect).
 *
 * The traveling-leg collectors deliberately skip anything that has landed, so without this a player who
 * reloads or navigates back mid-battle would see no line at all for a castle that is under attack — the
 * exact gap the persistent combat line exists to close. Emits one line per arrived leg plus, for direct
 * (non-leg) attacks, the main attacker's own line.
 */
export function collectEngagedCombatLineDraws(
  attacks: Array<
    MarchLegPendingApi & {
      status?: string
      fromQ?: number
      fromR?: number
      toQ?: number
      toR?: number
      arrivesAt?: string
      characterTemplateIds?: string[]
    }
  >,
  defenderLabel: (atk: MarchLegPendingApi) => string,
): EngagedCombatLineDraw[] {
  const out: EngagedCombatLineDraw[] = []
  for (const atk of attacks) {
    if (!isEngagedCombatLineStatus(atk.status)) continue
    const mvmAttackType = atk.attackType === 'tower_pvp' ? 'tower_pvp' : 'castle'
    const label = defenderLabel(atk)

    for (const leg of atk.marchLegs || []) {
      if (String(leg.status || '').toLowerCase() !== 'arrived') continue
      const legId = String(leg.legId || '').trim()
      if (!legId) continue
      const fromQ = Number(leg.fromQ)
      const fromR = Number(leg.fromR)
      const toQ = Number(leg.toQ)
      const toR = Number(leg.toR)
      if (![fromQ, fromR, toQ, toR].every(Number.isFinite)) continue
      const isDefLeg = leg.side === 'defender'
      out.push({
        attackId: atk.attackId,
        arrowKeySuffix: legId,
        fromQ,
        fromR,
        toQ,
        toR,
        targetOwner: isDefLeg ? atk.attackerWallet : atk.defenderWallet,
        targetName: isDefLeg ? '' : label,
        templateIds: leg.characterTemplateIds,
        mvmAttackType,
        engagedAtMs: parseIsoMs(leg.arrivesAt),
      })
    }

    /** Direct attack (or a rally whose leader has no leg): the top-level march IS the attacker's line. */
    if (!attackerUsesMarchLegsApi(atk)) {
      const fromQ = Number(atk.fromQ)
      const fromR = Number(atk.fromR)
      const toQ = Number(atk.toQ)
      const toR = Number(atk.toR)
      if (![fromQ, fromR, toQ, toR].every(Number.isFinite)) continue
      out.push({
        attackId: atk.attackId,
        fromQ,
        fromR,
        toQ,
        toR,
        targetOwner: atk.defenderWallet,
        targetName: label,
        templateIds: atk.characterTemplateIds,
        mvmAttackType,
        engagedAtMs: parseIsoMs(atk.arrivesAt),
      })
    }
  }
  return out
}

/** Draw params for a per-ally home march on the world map (reconnect / pending-hydrate restore). */
export type AllyReturnArrowDraw = {
  attackId: string
  walletAddress: string
  /** Per-ally arrow key suffix — `ally_<wallet>`; never starts with `leg_`, so leg removers skip it. */
  suffix: string
  fromQ: number
  fromR: number
  toQ: number
  toR: number
  travelTimeMinutes: number
  templateIds?: string[]
  arrowTime: MvmArrowRestoreTime
}

/**
 * Build draw params for each in-flight ally home march. Reuses the `returning` arrow-window math so the
 * head of the line resumes mid-route after navigation / reconnect. Only emits allies still in flight.
 */
export function collectAllyReturnMarchDraws(
  attacks: Array<{ attackId: string; allyReturnMarches?: AllyReturnMarchApi[] }>,
  now: number,
  arrivalBufferMs: number,
): AllyReturnArrowDraw[] {
  const out: AllyReturnArrowDraw[] = []
  for (const atk of attacks) {
    for (const m of atk.allyReturnMarches || []) {
      const wallet = normWalletMvM(m.walletAddress)
      if (!wallet) continue
      const fromQ = Number(m.fromQ)
      const fromR = Number(m.fromR)
      const toQ = Number(m.toQ)
      const toR = Number(m.toR)
      if (![fromQ, fromR, toQ, toR].every(Number.isFinite)) continue
      const retMs = parseIsoMs(m.returnArrivesAt)
      if (!Number.isFinite(retMs) || retMs <= now) continue
      const arrowTime = mvmRestoreArrowTimeWindow(
        { status: 'returning', returnArrivesAt: m.returnArrivesAt, marchTravelTimeMs: m.marchMs },
        now,
        arrivalBufferMs,
        'returning',
      )
      if (!arrowTime) continue
      out.push({
        attackId: atk.attackId,
        walletAddress: wallet,
        suffix: `ally_${wallet}`,
        fromQ,
        fromR,
        toQ,
        toR,
        travelTimeMinutes: Math.max((retMs + arrivalBufferMs - now) / 60000, 0.01),
        templateIds: m.characterTemplateIds,
        arrowTime,
      })
    }
  }
  return out
}

/** Engine surface for drawing per-leg march arrows on the world map. */
/**
 * Structural subset of the world-map engine used for march-leg arrows. `addAttackArrow`'s parameter list
 * MUST stay positionally identical to `Canvas2DWorldMapEngine.addAttackArrow` — params 12/13 are the
 * `barbarianRaid`/`defenseTowerRaid` objects (this helper always passes `undefined` there), NOT wallet
 * strings. A prior version declared them as `attackerWallet`/`attackerName: string`, which made the
 * concrete engine non-assignable to this type once callers stopped widening it to the render-engine union.
 */
export type MarchLegArrowEngine = {
  removeLegMarchArrowsForAttack: (attackId: string) => void
  /**
   * Promote finished march lines of this attack to combat lines. Called before pruning so a leg that
   * just landed is kept as an engaged line instead of being dropped for no longer being `traveling`.
   */
  engageCombatLinesForAttackId?: (attackId: string) => void
  removeAttackArrow: (toQ: number, toR: number, attackId?: string) => void
  addAttackArrow: (
    fromQ: number,
    fromR: number,
    toQ: number,
    toR: number,
    travelTimeMinutes: number,
    targetOwner?: string,
    targetName?: string,
    charIds?: string[],
    abilityBudget?: number,
    templateIds?: string[],
    attackId?: string,
    barbarianRaid?: { worldId: string; villageId: string; pveRound: number; characterIds?: string[] },
    defenseTowerRaid?: {
      worldId: string
      crossingKey: string
      towerQ: number
      towerR: number
      characterIds?: string[]
    },
    isReturning?: boolean,
    arrowKeySuffix?: string,
    mvmAttackType?: 'castle' | 'tower_pvp',
    arrowTime?: { startTimeMs: number; endTimeMs: number },
    marchWaypointHexes?: Array<{ q: number; r: number }> | null,
  ) => void
}

/** Apply traveling `marchLegs` from a world socket / REST attack snapshot. */
export function syncMarchLegArrowsFromWorldAttack(
  engine: MarchLegArrowEngine,
  attack: {
    attackId: string
    attackerWallet?: string
    defenderWallet?: string
    defenderName?: string
    rallyMode?: boolean
    marchLegs?: MarchLegApi[]
    toQ?: number
    toR?: number
    travelTimeMs?: number
    marchTravelTimeMs?: number
  },
  opts: {
    now: number
    arrivalBufferMs: number
    viewerWallet?: string
    defenderLabel?: string
  },
): void {
  if (!Array.isArray(attack.marchLegs) || attack.marchLegs.length === 0) return
  /**
   * Legs that have reached the castle become combat lines FIRST, because the prune below deletes every
   * non-engaged leg line and the redraw only re-adds `traveling` ones. Without this, a defender ally's
   * line vanished off the map the instant they arrived — the army was standing in the siege, but the
   * only thing left drawn was the attacker. `removeLegMarchArrowsForAttack` skips engaged lines, so
   * promoting them here is what keeps them on screen until the battle actually ends.
   */
  engine.engageCombatLinesForAttackId?.(attack.attackId)
  engine.removeLegMarchArrowsForAttack(attack.attackId)
  /**
   * Only drop the default (non-suffixed) attack arrow when the ATTACKER marches as a leg (rally) — that
   * legacy single line would otherwise stack on the leader leg. On a direct attack the default arrow IS
   * the attacker's march, so keep it; we are only adding the defender's reinforcement leg here.
   */
  if (attack.toQ != null && attack.toR != null && attackerUsesMarchLegsApi(attack)) {
    engine.removeAttackArrow(attack.toQ, attack.toR, attack.attackId)
  }
  const atkLike: MarchLegPendingApi = {
    attackId: attack.attackId,
    attackerWallet: attack.attackerWallet,
    defenderWallet: attack.defenderWallet,
    rallyMode: attack.rallyMode,
    marchLegs: attack.marchLegs,
    travelTimeMs: attack.travelTimeMs,
    marchTravelTimeMs: attack.marchTravelTimeMs ?? attack.travelTimeMs,
  }
  const label =
    opts.defenderLabel ||
    (attack.defenderName || '').trim() ||
    (attack.defenderWallet ? `${attack.defenderWallet.slice(0, 6)}…` : '')
  const draws = opts.viewerWallet
    ? collectMarchLegArrowDraws(
        [atkLike],
        opts.viewerWallet,
        opts.now,
        opts.arrivalBufferMs,
        () => label,
      )
    : collectPublicMarchLegArrowDraws([atkLike], opts.now, opts.arrivalBufferMs, () => label)
  for (const draw of draws) {
    engine.addAttackArrow(
      draw.fromQ,
      draw.fromR,
      draw.toQ,
      draw.toR,
      draw.travelTimeMinutes,
      draw.targetOwner,
      draw.targetName,
      draw.charIds,
      draw.abilityBudget,
      draw.templateIds,
      draw.attackId,
      undefined,
      undefined,
      false,
      draw.legId,
      draw.mvmAttackType,
      draw.arrowTime,
      undefined,
    )
  }
}

/**
 * When re-adding a march from REST/socket snapshots, use server times so the head of the line
 * resumes mid-route instead of restarting from the attacker castle.
 */
export type MvmArrowRestoreTime = {
  startTimeMs: number
  endTimeMs: number
}

function parseIsoMs(s: string | undefined): number {
  if (!s) return NaN
  const t = new Date(s).getTime()
  return Number.isFinite(t) ? t : NaN
}

function mvmStatusLower(status: string | undefined): string {
  return String(status || '').trim().toLowerCase()
}

/**
 * Return-leg ETA: prefer `returnArrivesAt`, fall back to `arrivesAt` when the server mirrors the return
 * arrival there for legacy clients (`WorldAttackData` / pending-attacks snapshots).
 */
export function mvmEffectiveReturnArrivalIso(
  like: Pick<MvMPendingLike, 'returnArrivesAt' | 'arrivesAt'>,
): string | undefined {
  const r = String(like.returnArrivesAt || '').trim()
  if (r) return r
  const a = String(like.arrivesAt || '').trim()
  return a || undefined
}

export function effectiveMvmRallyJoinerCount(atk: MvMPendingLike): number {
  let n = 0
  for (const j of atk.rallyJoiners || []) {
    const ids = j.selectedCharacterIds
    if (Array.isArray(ids) && ids.length > 0) n += 1
  }
  return n
}

export function mvmRallyPhaseEndMs(atk: MvMPendingLike): number {
  let ms = parseIsoMs(atk.rallyPhaseEndAt)
  if (Number.isFinite(ms)) return ms
  if (atk.status === 'rallying') {
    ms = parseIsoMs(atk.arrivesAt)
    if (Number.isFinite(ms)) return ms
  }
  const started = parseIsoMs(atk.startedAt)
  if (Number.isFinite(started)) return started + MVM_RALLY_JOIN_WINDOW_MS
  return NaN
}

/**
 * Arrival countdown target (ms): rally window end while gathering; rally end + march ms once the window
 * closed and joiners exist but DB may still say `rallying` until the scheduler runs.
 */
export function mvmPendingArrivalEndMs(atk: MvMPendingLike, now = Date.now()): number {
  if (mvmStatusLower(atk.status) === 'returning') {
    const ret = parseIsoMs(mvmEffectiveReturnArrivalIso(atk))
    if (Number.isFinite(ret)) return ret
  }

  const marchMs = atk.marchTravelTimeMs ?? atk.travelTimeMs ?? 0
  const rallyEnd = mvmRallyPhaseEndMs(atk)

  if (atk.status !== 'rallying') {
    return parseIsoMs(atk.arrivesAt) || 0
  }

  const joiners = effectiveMvmRallyJoinerCount(atk)
  if (Number.isFinite(rallyEnd) && now >= rallyEnd && joiners > 0 && marchMs > 0) {
    return rallyEnd + marchMs
  }
  if (Number.isFinite(rallyEnd)) return rallyEnd
  const fallback = parseIsoMs(atk.arrivesAt)
  return Number.isFinite(fallback) ? fallback : now
}

export function mvmPendingIsSyntheticMarch(atk: MvMPendingLike, now = Date.now()): boolean {
  if (atk.status !== 'rallying') return false
  const marchMs = atk.marchTravelTimeMs ?? atk.travelTimeMs ?? 0
  const rallyEnd = mvmRallyPhaseEndMs(atk)
  const joiners = effectiveMvmRallyJoinerCount(atk)
  return Number.isFinite(rallyEnd) && now >= rallyEnd && joiners > 0 && marchMs > 0
}

export function mvmPendingDisplayPhase(atk: MvMPendingLike, now = Date.now()): 'rally' | 'march' {
  if (atk.status !== 'rallying') return 'march'
  if (mvmPendingIsSyntheticMarch(atk, now)) return 'march'
  return 'rally'
}

/**
 * Hide march / alliance roster rows when the army is home: stale `returning` + past `returnArrivesAt`
 * (server may lag before removing the pending row).
 */
export function mvmPendingRowStillOnMarchPanel(like: MvMPendingLike | null | undefined, now = Date.now()): boolean {
  if (!like?.status) return true
  if (mvmStatusLower(like.status) !== 'returning') return true
  const ret = parseIsoMs(mvmEffectiveReturnArrivalIso(like))
  if (!Number.isFinite(ret)) return true
  return ret > now
}

/**
 * Build absolute start/end times for the march line when hydrating from GET /mvm/pending-attacks.
 * `arrivalBufferMs` should match the world-map arrow buffer (e.g. `ARROW_ARRIVAL_BUFFER_MS` on `WorldMapPage`).
 */
export function mvmRestoreArrowTimeWindow(
  like: MvMPendingLike,
  now: number,
  arrivalBufferMs: number,
  leg: 'outbound' | 'returning',
): MvmArrowRestoreTime | null {
  const buffer = arrivalBufferMs

  if (leg === 'returning') {
    const retEnd = parseIsoMs(mvmEffectiveReturnArrivalIso(like))
    if (!Number.isFinite(retEnd) || retEnd <= now) return null
    const endTimeMs = retEnd + buffer
    let marchLen = Number(like.marchTravelTimeMs ?? like.travelTimeMs ?? 0)
    if (!Number.isFinite(marchLen) || marchLen < 0) marchLen = 0
    if (marchLen > 0) return { startTimeMs: retEnd - marchLen, endTimeMs }
    /**
     * Some API snapshots omit march duration for `returning`; using `startedAt` is wrong (original outbound start).
     * Anchor the return leg to "now" so the line is visible for the full remaining time to `returnArrivesAt`.
     */
    const remaining = Math.max(1, retEnd - now)
    return { startTimeMs: retEnd - remaining, endTimeMs }
  }

  let marchMs = Number(like.marchTravelTimeMs ?? like.travelTimeMs ?? 0)
  if (!Number.isFinite(marchMs) || marchMs < 0) marchMs = 0

  const endMsRaw = mvmPendingArrivalEndMs(like, now)
  if (!Number.isFinite(endMsRaw) || endMsRaw <= now) return null
  const endTimeMs = endMsRaw + buffer

  if (mvmPendingIsSyntheticMarch(like, now)) {
    const rallyEnd = mvmRallyPhaseEndMs(like)
    if (Number.isFinite(rallyEnd) && marchMs > 0) {
      return { startTimeMs: rallyEnd, endTimeMs: rallyEnd + marchMs + buffer }
    }
  }

  const arrivesAt = parseIsoMs(like.arrivesAt)
  if (Number.isFinite(arrivesAt) && marchMs > 0) {
    return { startTimeMs: arrivesAt - marchMs, endTimeMs }
  }

  const started = parseIsoMs(like.startedAt)
  if (Number.isFinite(started) && endTimeMs > started) {
    return { startTimeMs: started, endTimeMs }
  }

  return null
}

/** GET active world attacks (`WorldAttackData`) — uses server `travelTimeMs` + `arrivesAt`. */
export function mvmRestoreWorldAttackArrowTime(
  arrivesAtIso: string,
  travelTimeMs: number,
  startedAtIso: string | undefined,
  now: number,
  arrivalBufferMs: number,
): MvmArrowRestoreTime | null {
  const arr = parseIsoMs(arrivesAtIso)
  if (!Number.isFinite(arr) || arr <= now) return null
  const endTimeMs = arr + arrivalBufferMs
  const marchMs = travelTimeMs > 0 ? travelTimeMs : 0
  if (marchMs > 0) return { startTimeMs: arr - marchMs, endTimeMs }
  const started = parseIsoMs(startedAtIso)
  if (Number.isFinite(started) && endTimeMs > started) return { startTimeMs: started, endTimeMs }
  return null
}

/** Single march leg when you only have arrival ISO + duration (e.g. tower defender to tower). */
export function mvmRestoreSimpleMarchWindow(opts: {
  arrivalEndMs: number
  marchDurationMs: number
  now: number
  arrivalBufferMs: number
}): MvmArrowRestoreTime | null {
  const { arrivalEndMs, marchDurationMs, now, arrivalBufferMs } = opts
  if (!Number.isFinite(arrivalEndMs) || arrivalEndMs <= now) return null
  const endTimeMs = arrivalEndMs + arrivalBufferMs
  if (marchDurationMs > 0) return { startTimeMs: arrivalEndMs - marchDurationMs, endTimeMs }
  return null
}

/**
 * Whether REST sync may open the defender character-picker modal.
 * Only after server `character_selection` (attacker attack-arrived or stuck-`arrived` promotion) —
 * not while status is still `traveling` or server-only `arrived`.
 * Socket `mvm:attack-arrived` still opens the modal when prep actually starts.
 */
export function mvmMayShowDefenderCharacterPickerFromRest(atk: MvMPendingLike, now = Date.now()): boolean {
  const st = String(atk.status || '')
  if (st !== 'character_selection') return false
  const endMs = mvmPendingArrivalEndMs(atk, now)
  if (!Number.isFinite(endMs) || endMs <= 0) {
    const t = atk.arrivesAt ? new Date(atk.arrivesAt).getTime() : NaN
    if (!Number.isFinite(t)) return true
    return now + MVM_TRAVEL_ARRIVAL_TOLERANCE_MS >= t
  }
  return now + MVM_TRAVEL_ARRIVAL_TOLERANCE_MS >= endMs
}
